// ---------------------------------------------------------------------------
// Form entity types (camelCase — normalized from backend PascalCase)
// ---------------------------------------------------------------------------

/**
 * Minimal form reference used by admin statistics and other features.
 * Kept for backward compatibility with existing imports.
 */
export type Form = {
	Id: number;
	Name: string;
};

/**
 * A form entity as used by the React frontend.
 * Linked from a Container V2 Form module via its containerModuleGuid (formKey).
 */
export type FormData = {
	id: number;
	name: string;
	nameTranslations: FormTranslation[];
	containerModuleGuid: string;
	createdDateUtc: string | null;
	publishDateUtc: string | null;
	expiresDateUtc: string | null;
	updatedDateUtc: string | null;
	fields: FormField[];
	fieldGroups: FormFieldGroup[];
	wizardSteps: FormWizardStep[];
	settings: FormSetting[];
	plugin: FormPlugin | null;
	pluginAssemblyName: string | null;
	pluginClassName: string | null;
	pluginSettings: Record<string, unknown> | null;
	emailRecipients: string[] | null;
	sendConfirmEmail: boolean;
	submissionCount: number;
	currentUserSubmissionCount: number;
	giveXp: boolean;
	giveVirtualCurrency: boolean;
	/** Dates the user has not yet submitted for (once-per-day mode). */
	missingSubmissionDatesUtc?: string[];
};

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

export type FormFieldTypeKey =
	| 'input'
	| 'textarea'
	| 'checkbox'
	| 'checkboxlist'
	| 'radiobuttonlist'
	| 'dropdownlist'
	| 'slider'
	| 'label'
	| 'recaptcha'
	| 'phonecountrycode'
	| 'video'
	| 'fileupload';

export type FormFieldDataType = 'email' | 'username' | 'company' | 'password' | 'firstname' | 'lastname' | 'phone' | 'newsletter';

/**
 * A single field within a form.
 * Maps from backend PascalCase fields (Id, Key, Type, Order, Width, IsMandatory,
 * Placeholder, FormFieldGroupKey, FormWizardStepKey, Options, CustomSettings).
 */
export type FormField = {
	id: number;
	key: string;
	typeKey: FormFieldTypeKey;
	label: string;
	placeholder: string;
	required: boolean;
	order: number;
	/** CSS width class: '' (100%) | 'fw50' | 'fw33' | 'fw25' | 'fw20' */
	widthClass: string;
	dataType: FormFieldDataType | null;
	groupKey: string;
	wizardStepKey: string;
	translations: FormFieldTranslation[];
	options: FormFieldOption[];
	listeners: FormFieldListener[];
	/** Type-specific settings (e.g. min, max, step for slider; type for input). */
	settings: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Translations
// ---------------------------------------------------------------------------

/** Translation type discriminator values. */
export const TRANSLATION_TYPE = {
	NAME: 1,
	PLACEHOLDER: 2,
	DESCRIPTION: 4,
	IMAGE: 5,
} as const;

export type FormTranslation = {
	cultureId: string;
	type: number;
	value: string;
};

export type FormFieldTranslation = FormTranslation & {
	fieldKey: string;
};

// ---------------------------------------------------------------------------
// Field options (checkboxlist, radiobuttonlist, dropdownlist)
// ---------------------------------------------------------------------------

export type FormFieldOption = {
	id: string;
	formFieldId: string;
	value: number;
	translations: FormTranslation[];
	settings: {
		preselected?: boolean;
		disabled?: boolean;
	};
};

// ---------------------------------------------------------------------------
// Field listeners (conditional show/hide)
// ---------------------------------------------------------------------------

export type FormFieldListener = {
	/** Key of the field to watch. */
	key: string;
	/** Regex pattern to match against the watched field's value. */
	value: string;
	action: 'show' | 'hide';
};

// ---------------------------------------------------------------------------
// Field groups
// ---------------------------------------------------------------------------

export type FormFieldGroup = {
	key: string;
	name: string;
	formId: number;
	translations: FormTranslation[];
	order: number;
	settings: FormFieldGroupSettings;
};

export type FormFieldGroupSettings = {
	expandable?: boolean;
	startExpanded?: boolean;
	borderWidth?: string;
	borderColor?: string;
	headerBackgroundColor?: string;
	backgroundColor?: string;
	headerTextColor?: string;
	textColor?: string;
	imageUrl?: string;
	wizardStepKey?: string;
};

// ---------------------------------------------------------------------------
// Wizard steps
// ---------------------------------------------------------------------------

export type FormWizardStep = {
	key: string;
	name: string | null;
	formId: number;
	translations: FormTranslation[];
	order: number;
	settings: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Form settings
// ---------------------------------------------------------------------------

export type FormSetting = {
	key: string;
	/** Stored as string on the backend; parse as needed. */
	value: string;
};

/** Well-known setting keys used by the form system. */
export const FORM_SETTING_KEYS = {
	TRACKING_ID: 'TRACKING_ID',
	SEND_EMAIL: 'SEND_EMAIL',
	ALLOW_MULTIPLE_SUBMISSIONS: 'ALLOW_MULTIPLE_SUBMISSIONS_FROM_SAME_USER',
	ONCE_PER_LOGGED_IN_DAY: 'ONCE_PER_LOGGED_IN_DAY',
	MAX_ONCE_PER_DAY: 'MAX_ONCE_PER_DAY',
	EFFECT_ON_SUBMIT: 'EFFECT_ON_SUBMIT',
} as const;

// ---------------------------------------------------------------------------
// Plugins
// ---------------------------------------------------------------------------

export type FormPlugin = {
	key: string;
	name: string;
	assemblyName: string | null;
	className: string | null;
	fieldName: string | null;
	fieldTypes: unknown[];
	pluginJavascriptSrc: string | null;
};

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------

export type FormSubmission = {
	formId: number;
	objectKey?: string;
	objectId?: number;
	values: FormSubmissionValue[];
};

export type FormSubmissionValue = {
	formFieldKey: string;
	formFieldId: number;
	value: string;
};

/** Wire response from the existing SubmitForm endpoint (not a saved submission). */
export type LegacyFormSubmitResponse = {
	Success: boolean;
	Message?: string;
	ObjectId?: number;
	ExtraInfo?: { IsPassed?: boolean };
};

export type FormSubmitResult = {
	success: true;
	isPassed?: boolean;
};

export type FormSubmissionResult = {
	id: number;
	formId: number;
	submittedDateUtc: string;
	userId: number | null;
	isArchived: boolean;
	comment: string | null;
	values: FormSubmissionValue[];
	/** Actions to perform after submission (backend response). */
	actions?: FormSubmissionAction[];
};

export type FormSubmissionAction = {
	messagePhrase?: string;
	url?: string;
};

// ---------------------------------------------------------------------------
// Legacy API types (PascalCase — as returned by the backend)
// ---------------------------------------------------------------------------

export type LegacyApiForm = {
	Id: number;
	Name: string;
	NameTranslations: LegacyApiFormTranslation[];
	ContainerModuleGuid: string;
	CreatedDateUtc: string | null;
	PublishDateUtc: string | null;
	ExpiresDateUtc: string | null;
	UpdatedDateUtc: string | null;
	Fields: LegacyApiFormField[];
	FieldGroups: LegacyApiFormFieldGroup[];
	WizardSteps: LegacyApiFormWizardStep[];
	Settings: LegacyApiFormSetting[];
	Plugin: LegacyApiFormPlugin | null;
	PluginAssemblyName: string | null;
	PluginClassName: string | null;
	PluginSettings: Record<string, unknown> | null;
	EmailRecipients: string[] | null;
	SendConfirmEmail: boolean;
	SubmissionCount: number;
	CurrentUserSubmissionCount: number;
	GiveXp: boolean;
	GiveVirtualCurrency: boolean;
	MissingSubmissionDatesUtc?: string[];
};

export type LegacyApiFormTranslation = {
	CultureId: string;
	Type: number;
	Value: string;
	FieldKey?: string;
};

export type LegacyApiFormField = {
	Id: number;
	Key: string;
	Type: string;
	Order: number;
	Width: string;
	Name: string;
	IsMandatory: boolean;
	Placeholder: string;
	FormFieldGroupKey: string;
	FormWizardStepKey: string;
	Options: LegacyApiFormFieldOption[];
	NameTranslations: LegacyApiFormTranslation[];
	PlaceholderTranslations: LegacyApiFormTranslation[];
	/** JSON string with TemplateOptions and Data sub-objects. */
	CustomSettings: string;
};

export type LegacyApiFormFieldOption = {
	Id: string;
	FormFieldId: string;
	Value: number;
	Translations: LegacyApiFormTranslation[];
	Settings: Record<string, unknown>;
};

export type LegacyApiFormFieldGroup = {
	Key: string;
	Name: string;
	FormId: number;
	Translations: LegacyApiFormTranslation[];
	Order: number;
	/** JSON string of settings. */
	Settings: string;
};

export type LegacyApiFormWizardStep = {
	Key: string;
	Name: string | null;
	FormId: number;
	Translations: LegacyApiFormTranslation[];
	Order: number;
	/** JSON string of settings. */
	Settings: string;
};

export type LegacyApiFormSetting = {
	Key: string;
	Value: string;
};

export type LegacyApiFormPlugin = {
	Key: string;
	Name: string;
	AssemblyName: string | null;
	ClassName: string | null;
	FieldName: string | null;
	FieldTypes: unknown[];
	PluginJavascriptSrc: string | null;
};
